/**
 * Sistema de Cache Inteligente para Gráficos
 * 
 * Funcionalidades:
 * - Cache em memória para dados processados (Kalman + pesos)
 * - Cache persistente para dados brutos da API
 * - Auto-limpeza baseada em timestamp
 * - Cache por parâmetros (região, filtros, etc.)
 * - Invalidação inteligente quando dados mudam
 */

// =====================================================================
// CONFIGURAÇÕES DO CACHE
// =====================================================================
const CACHE_CONFIG = {
  // Tempo de vida do cache em memória (5 minutos)
  MEMORY_TTL: 5 * 60 * 1000,
  
  // Tempo de vida do cache persistente (30 minutos)
  PERSISTENT_TTL: 30 * 60 * 1000,
  
  // Máximo de itens em memória
  MAX_MEMORY_ITEMS: 50,
  
  // Prefixo para localStorage
  STORAGE_PREFIX: 'chart_cache_',
  
  // Chaves das APIs que são cacheadas
  API_KEYS: ['dados', 'institutos', 'metodos', 'intencoes']
};

// =====================================================================
// CACHE EM MEMÓRIA
// =====================================================================
class MemoryCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
  }

  /**
   * Gera chave única baseada nos parâmetros
   */
  generateKey(type, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${JSON.stringify(params[key])}`)
      .join('|');
    
    return `${type}_${sortedParams}`;
  }

  /**
   * Verifica se o item está válido (não expirado)
   */
  isValid(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return false;
    
    return Date.now() - timestamp < CACHE_CONFIG.MEMORY_TTL;
  }

  /**
   * Obtém item do cache se válido
   */
  get(key) {
    if (!this.isValid(key)) {
      this.delete(key);
      return null;
    }
    const result = this.cache.get(key);
    return result;
  }

  /**
   * Armazena item no cache
   */
  set(key, value) {
    // Limpa cache se exceder limite
    if (this.cache.size >= CACHE_CONFIG.MAX_MEMORY_ITEMS) {
      this.cleanup();
    }
    
    this.cache.set(key, value);
    this.timestamps.set(key, Date.now());
  }

  /**
   * Remove item do cache
   */
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  /**
   * Limpa itens expirados
   */
  cleanup() {
    const now = Date.now();
    const expiredKeys = [];
    
    for (const [key, timestamp] of this.timestamps) {
      if (now - timestamp >= CACHE_CONFIG.MEMORY_TTL) {
        expiredKeys.push(key);
      }
    }
    
    expiredKeys.forEach(key => this.delete(key));
  }

  /**
   * Limpa todo o cache
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }
}

// =====================================================================
// CACHE PERSISTENTE
// =====================================================================
class PersistentCache {
  constructor() {
    this.storage = localStorage;
  }

  /**
   * Gera chave para localStorage
   */
  generateKey(type, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}:${JSON.stringify(params[key])}`)
      .join('|');
    
    return `${CACHE_CONFIG.STORAGE_PREFIX}${type}_${sortedParams}`;
  }

  /**
   * Verifica se o item está válido
   */
  isValid(key) {
    try {
      const item = this.storage.getItem(key);
      if (!item) return false;
      
      const { timestamp } = JSON.parse(item);
      return Date.now() - timestamp < CACHE_CONFIG.PERSISTENT_TTL;
    } catch {
      return false;
    }
  }

  /**
   * Obtém item do cache persistente
   */
  get(key) {
    if (!this.isValid(key)) {
      this.delete(key);
      return null;
    }
    
    try {
      const item = this.storage.getItem(key);
      const { data } = JSON.parse(item);
      return data;
    } catch {
      this.delete(key);
      return null;
    }
  }

  /**
   * Armazena item no cache persistente
   */
  set(key, data) {
    try {
      const item = {
        data,
        timestamp: Date.now()
      };
      this.storage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.warn('Erro ao salvar no cache persistente:', error);
    }
  }

  /**
   * Remove item do cache persistente
   */
  delete(key) {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.warn('Erro ao remover do cache persistente:', error);
    }
  }

  /**
   * Limpa cache expirado
   */
  cleanup() {
    try {
      const keys = Object.keys(this.storage);
      const expiredKeys = [];
      
      for (const key of keys) {
        if (key.startsWith(CACHE_CONFIG.STORAGE_PREFIX)) {
          if (!this.isValid(key)) {
            expiredKeys.push(key);
          }
        }
      }
      
      expiredKeys.forEach(key => this.delete(key));
    } catch (error) {
      console.warn('Erro ao limpar cache persistente:', error);
    }
  }

  /**
   * Limpa todo o cache persistente
   */
  clear() {
    try {
      const keys = Object.keys(this.storage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_CONFIG.STORAGE_PREFIX)) {
          this.storage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Erro ao limpar cache persistente:', error);
    }
  }
}

// =====================================================================
// SISTEMA DE CACHE PRINCIPAL
// =====================================================================
class ChartCache {
  constructor() {
    this.memoryCache = new MemoryCache();
    this.persistentCache = new PersistentCache();
    
    // Limpa cache expirado na inicialização
    this.cleanup();
    
    // Limpa cache periodicamente (a cada 10 minutos)
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 10 * 60 * 1000);
  }

  /**
   * Obtém dados da API com cache
   */
  async getApiData(endpoint, params = {}) {
    const key = this.persistentCache.generateKey(endpoint, params);
    
    // Tenta cache persistente primeiro
    let data = this.persistentCache.get(key);
    if (data) {
      return data;
    }
    
    // Se não tem cache, busca da API
    try {
      const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
      const url = `${BASE}/api/${endpoint}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }
      
      data = await response.json();
      
      // Salva no cache persistente
      this.persistentCache.set(key, data);
      
      return data;
    } catch (error) {
      console.error(`Erro ao buscar dados de ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Obtém dados processados com cache
   */
  getProcessedData(type, params = {}, processor) {
    const key = this.memoryCache.generateKey(type, params);
    
    // Tenta cache em memória primeiro
    let data = this.memoryCache.get(key);
    if (data) {
      return data;
    }
    
    // Se não tem cache, processa os dados
    data = processor();
    
    // Salva no cache em memória
    this.memoryCache.set(key, data);
    
    return data;
  }

  /**
   * Invalida cache específico
   */
  invalidate(type, params = {}) {
    const memoryKey = this.memoryCache.generateKey(type, params);
    const persistentKey = this.persistentCache.generateKey(type, params);
    
    this.memoryCache.delete(memoryKey);
    this.persistentCache.delete(persistentKey);
  }

  /**
   * Invalida todo o cache de um tipo
   */
  invalidateType(type) {
    // Limpa cache em memória
    this.memoryCache.clear();
    
    // Limpa cache persistente do tipo
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(`${CACHE_CONFIG.STORAGE_PREFIX}${type}_`)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Erro ao invalidar cache do tipo:', error);
    }
  }

  /**
   * Limpa cache expirado
   */
  cleanup() {
    this.memoryCache.cleanup();
    this.persistentCache.cleanup();
  }

  /**
   * Limpa todo o cache
   */
  clear() {
    this.memoryCache.clear();
    this.persistentCache.clear();
  }

  /**
   * Destrói o sistema de cache
   */
  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clear();
  }
}

// =====================================================================
// INSTÂNCIA SINGLETON
// =====================================================================
const chartCache = new ChartCache();

// =====================================================================
// EXPORTS
// =====================================================================
export default chartCache;
export { MemoryCache, PersistentCache, ChartCache };
export { CACHE_CONFIG };